import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Alert, CircularProgress, Breadcrumbs, Link, Avatar, IconButton,
    Tooltip, InputAdornment, Divider, Autocomplete
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PreviewIcon from '@mui/icons-material/Preview';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import WorkIcon from '@mui/icons-material/Work';
import InfoIcon from '@mui/icons-material/Info';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

export const PlayersCreatePage = () => {
    const navigate = useNavigate();
    const notify = useNotify();

    // Stati del form
    const [formData, setFormData] = React.useState({
        nomeGiocatore: '',
        teamId: '',
        ruoloId: '',
        foto: '',
        ordinamento: '',
        note: '',
        flVisibile: true,
        flAttivo: true
    });

    // Stati di controllo
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [touched, setTouched] = React.useState({});

    // Dati per dropdown
    const [teams, setTeams] = React.useState([]);
    const [ruoli, setRuoli] = React.useState([]);
    const [leghe, setLeghe] = React.useState([]);
    const [loadingTeams, setLoadingTeams] = React.useState(true);
    const [loadingRuoli, setLoadingRuoli] = React.useState(true);
    const [loadingLeghe, setLoadingLeghe] = React.useState(false);
    const [teamSearchInput, setTeamSearchInput] = React.useState('');
    const [teamSearchOpen, setTeamSearchOpen] = React.useState(false);
    const [selectedLegaId, setSelectedLegaId] = React.useState(null);

    // Preview foto
    const [fotoPreview, setFotoPreview] = React.useState('');
    const [fotoError, setFotoError] = React.useState(false);

    // Carica leghe/campionati
    const loadLeghe = async () => {
        try {
            setLoadingLeghe(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-lega`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    $select: ['id', 'nome'],
                    flVisibile: 1,
                    flAttivo: 1,
                    $sort: { nome: 1 }
                }
            });
            setLeghe(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento leghe:", error);
            notify("Errore nel caricamento campionati", { type: 'error' });
        } finally {
            setLoadingLeghe(false);
        }
    };

    // Carica teams con supporto per ricerca e filtro lega
    const loadTeams = async (searchTerm = '', legaId = null) => {
        try {
            setLoadingTeams(true);
            const jwt = getJwt();
            const params = {
                $select: ['id', 'nome'],
                flVisibile: 1,
                flAttivo: 1,
                $sort: { nome: 1 },
                $limit: 50 // Limita i risultati per performance
            };

            // Aggiungi filtro lega se selezionata
            if (legaId) {
                params.legaId = legaId;
            }

            // Aggiungi filtro di ricerca se presente
            if (searchTerm && searchTerm.trim()) {
                params.nome = {
                    $like: `%${searchTerm.trim()}%`
                };
            }

            const response = await axios.get(`${host}/tbl-team`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params
            });
            // Filtra squadre con id 0
            const filteredTeams = (response.data.data || []).filter(team => team.id !== 0);
            setTeams(filteredTeams);
        } catch (error) {
            console.error("Errore nel caricamento teams:", error);
            notify("Errore nel caricamento squadre", { type: 'error' });
        } finally {
            setLoadingTeams(false);
        }
    };

    // Carica ruoli
    const loadRuoli = async () => {
        try {
            setLoadingRuoli(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-ruolo`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    $select: ['id', 'nome'],
                    $sort: { nome: 1 }
                }
            });
            setRuoli(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento ruoli:", error);
            notify("Errore nel caricamento ruoli", { type: 'error' });
        } finally {
            setLoadingRuoli(false);
        }
    };

    // Validazione campi
    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'nomeGiocatore':
                if (!value.trim()) {
                    newErrors.nomeGiocatore = 'Il nome del giocatore è obbligatorio';
                } else if (value.trim().length < 2) {
                    newErrors.nomeGiocatore = 'Il nome deve essere di almeno 2 caratteri';
                } else if (value.trim().length > 100) {
                    newErrors.nomeGiocatore = 'Il nome non può superare i 100 caratteri';
                } else {
                    delete newErrors.nomeGiocatore;
                }
                break;

            case 'teamId':
                if (!value) {
                    newErrors.teamId = 'La squadra è obbligatoria';
                } else {
                    delete newErrors.teamId;
                }
                break;

            case 'ruoloId':
                if (!value) {
                    newErrors.ruoloId = 'Il ruolo è obbligatorio';
                } else {
                    delete newErrors.ruoloId;
                }
                break;

            case 'foto':
                // Nessuna validazione: campo sempre opzionale
                delete newErrors.foto;
                break;

            case 'ordinamento':
                if (value && (isNaN(value) || value < 0)) {
                    newErrors.ordinamento = 'L\'ordinamento deve essere un numero positivo';
                } else {
                    delete newErrors.ordinamento;
                }
                break;

            case 'note':
                if (value && value.length > 500) {
                    newErrors.note = 'Le note non possono superare i 500 caratteri';
                } else {
                    delete newErrors.note;
                }
                break;

            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Gestisce il cambiamento dei campi
    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setTouched(prev => ({ ...prev, [name]: true }));

        // Validazione in tempo reale
        validateField(name, value);

        // Gestione preview foto
        if (name === 'foto') {
            if (value && value.trim()) {
                setFotoPreview(value.trim());
                setFotoError(false);
            } else {
                setFotoPreview('');
                setFotoError(false);
            }
        }
    };

    // Gestisce errore caricamento foto
    const handleFotoError = () => {
        setFotoError(true);
        // Non aggiungere errori a errors.foto, la preview non deve bloccare il salvataggio
    };

    // Gestisce successo caricamento foto
    const handleFotoLoad = () => {
        setFotoError(false);
        if (errors.foto && errors.foto.includes('Impossibile caricare')) {
            const newErrors = { ...errors };
            delete newErrors.foto;
            setErrors(newErrors);
        }
    };

    // Valida tutto il form
    const validateForm = () => {
        const fieldsToValidate = ['nomeGiocatore', 'teamId', 'ruoloId', 'foto', 'ordinamento', 'note'];
        let isValid = true;

        fieldsToValidate.forEach(field => {
            if (!validateField(field, formData[field])) {
                isValid = false;
            }
            setTouched(prev => ({ ...prev, [field]: true }));
        });

        return isValid;
    };

    // Salva il giocatore
    const handleSave = async () => {
        if (!validateForm()) {
            notify("Correggere gli errori nel form", { type: 'error' });
            return;
        }

        try {
            setSaving(true);
            const jwt = getJwt();

            // Prepara i dati per l'invio
            const dataToSend = {
                nome: formData.nomeGiocatore.trim(),
                teamId: parseInt(formData.teamId),
                ruoloId: parseInt(formData.ruoloId),
                foto: (formData.foto || "").trim(),
                ordinamento: formData.ordinamento ? parseInt(formData.ordinamento) : null,
                note: formData.note.trim() || null,
                flVisibile: formData.flVisibile,
                flAttivo: formData.flAttivo,
                flAnnullato: false
            };

            console.log("dataToSend : ", dataToSend);

            const response = await axios.post(
                `${host}/tbl-player`,
                dataToSend,
                { headers: { 'Authorization': `Bearer ${jwt}` } }
            );

            notify(`Giocatore "${formData.nomeGiocatore}" creato con successo`, { type: 'success' });

            // Naviga alla pagina di dettaglio del nuovo giocatore o lista
            if (response.data && response.data.id) {
                navigate(`/giocatori/${response.data.id}`);
            } else {
                navigate('/giocatori');
            }

        } catch (error) {
            console.error("Errore nella creazione giocatore:", error);

            if (error.response?.data?.message) {
                notify(`Errore: ${error.response.data.message}`, { type: 'error' });
            } else if (error.response?.status === 409) {
                notify("Un giocatore con questo nome esiste già in questa squadra", { type: 'error' });
            } else {
                notify("Errore nella creazione del giocatore", { type: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    // Reset form
    const handleReset = () => {
        setFormData({
            nomeGiocatore: '',
            teamId: '',
            ruoloId: '',
            foto: '',
            ordinamento: '',
            note: '',
            flVisibile: true,
            flAttivo: true
        });
        setErrors({});
        setTouched({});
        setFotoPreview('');
        setFotoError(false);
    };

    // Carica dati al mount
    React.useEffect(() => {
        loadLeghe();
        loadRuoli();

        // Ripristina lega selezionata da localStorage
        const savedLegaId = localStorage.getItem('selectedLegaId');
        if (savedLegaId) {
            setSelectedLegaId(savedLegaId);
            loadTeams('', savedLegaId);
        } else {
            loadTeams();
        }
    }, []);

    // Loading iniziale
    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                        Caricamento dati...
                    </Typography>
                </Box>
            </Box>
        );
    }

    const hasErrors = Object.keys(errors).length > 0;
    const canSave = formData.nomeGiocatore.trim() && formData.teamId && formData.ruoloId && !hasErrors;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >

                {/* Breadcrumb */}
                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate('/players')}
                        sx={{ textDecoration: 'none' }}
                    >
                        Giocatori
                    </Link>
                    <Typography variant="body2" color="text.primary">
                        Nuovo Giocatore
                    </Typography>
                </Breadcrumbs>

                {/* Header */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            👨‍💼 Crea Nuovo Giocatore
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Inserisci i dettagli per creare un nuovo giocatore
                        </Typography>
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/players')}
                    >
                        Indietro
                    </Button>
                </Box>

                {/* Form */}
                <Grid container spacing={3}>

                    {/* Colonna principale - Dati giocatore */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ p: 3 }}>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Informazioni Giocatore
                                    </Typography>
                                </Box>

                                <Grid container spacing={3}>

                                    {/* Nome giocatore */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Nome Giocatore *"
                                            value={formData.nomeGiocatore}
                                            onChange={(e) => handleFieldChange('nomeGiocatore', e.target.value)}
                                            error={touched.nomeGiocatore && !!errors.nomeGiocatore}
                                            helperText={touched.nomeGiocatore && errors.nomeGiocatore}
                                            placeholder="Inserisci il nome del giocatore"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PersonIcon />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    {/* Campionato (filtro opzionale) */}
                                    <Grid item xs={12} md={6}>
                                        <Autocomplete
                                            fullWidth
                                            options={leghe}
                                            value={leghe.find(l => l.id === selectedLegaId) || null}
                                            onChange={(event, newValue) => {
                                                const newLegaId = newValue ? newValue.id : null;
                                                setSelectedLegaId(newLegaId);
                                                setTeamSearchInput('');
                                                loadTeams('', newLegaId);
                                                // Salva in localStorage per persistenza
                                                if (newLegaId) {
                                                    localStorage.setItem('selectedLegaId', newLegaId);
                                                } else {
                                                    localStorage.removeItem('selectedLegaId');
                                                }
                                            }}
                                            getOptionLabel={(option) => option.nome || `Lega ${option.id}`}
                                            loading={loadingLeghe}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Campionato (opzionale)"
                                                    placeholder="Filtra per campionato..."
                                                    helperText="Lascia vuoto per vedere tutte le squadre"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {loadingLeghe ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                            noOptionsText="Nessun campionato trovato"
                                        />
                                    </Grid>

                                    {/* Team */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <GroupsIcon sx={{ color: '#1565c0', mt: 2 }} />
                                            <Autocomplete
                                                fullWidth
                                                open={teamSearchOpen}
                                                onOpen={() => {
                                                    setTeamSearchOpen(true);
                                                    if (teams.length === 0) {
                                                        loadTeams('', selectedLegaId);
                                                    }
                                                }}
                                                onClose={() => setTeamSearchOpen(false)}
                                                value={teams.find(t => t.id === formData.teamId) || null}
                                                onChange={(event, newValue) => {
                                                    handleFieldChange('teamId', newValue ? newValue.id : '');
                                                }}
                                                inputValue={teamSearchInput}
                                                onInputChange={(event, newInputValue, reason) => {
                                                    setTeamSearchInput(newInputValue);
                                                    if (reason === 'input') {
                                                        loadTeams(newInputValue, selectedLegaId);
                                                    } else if (reason === 'clear') {
                                                        // Reset search quando si clicca sulla X
                                                        setTeamSearchInput('');
                                                        loadTeams('', selectedLegaId);
                                                    }
                                                }}
                                                options={teams}
                                                getOptionLabel={(option) => option.nome || `Team ${option.id}`}
                                                loading={loadingTeams}
                                                disabled={loadingTeams && teams.length === 0}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                clearOnBlur={false}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Squadra *"
                                                        error={touched.teamId && !!errors.teamId}
                                                        helperText={touched.teamId && errors.teamId}
                                                        placeholder="Cerca squadra..."
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            endAdornment: (
                                                                <>
                                                                    {loadingTeams ? <CircularProgress color="inherit" size={20} /> : null}
                                                                    {params.InputProps.endAdornment}
                                                                </>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                                noOptionsText="Nessuna squadra trovata"
                                            />
                                        </Box>
                                    </Grid>

                                    {/* Ruolo */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <WorkIcon sx={{ color: '#e65100', mt: 2 }} />
                                            <FormControl
                                                fullWidth
                                                error={touched.ruoloId && !!errors.ruoloId}
                                            >
                                                <InputLabel>Ruolo *</InputLabel>
                                                <Select
                                                    value={formData.ruoloId}
                                                    label="Ruolo *"
                                                    onChange={(e) => handleFieldChange('ruoloId', e.target.value)}
                                                    disabled={loadingRuoli}
                                                >
                                                    {loadingRuoli ? (
                                                        <MenuItem disabled>
                                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                                            Caricamento...
                                                        </MenuItem>
                                                    ) : (
                                                        ruoli.map(ruolo => (
                                                            <MenuItem key={ruolo.id} value={ruolo.id}>
                                                                {ruolo.nome || `Ruolo ${ruolo.id}`}
                                                            </MenuItem>
                                                        ))
                                                    )}
                                                </Select>
                                                {touched.ruoloId && errors.ruoloId && (
                                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                        {errors.ruoloId}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        </Box>
                                    </Grid>

                                    {/* URL Foto */}
                                    <Grid item xs={12}>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            Alcuni servizi di hosting immagini (es. Google Drive, Dropbox, ecc.) possono <b>bloccare la visualizzazione dell’anteprima nel form</b> per motivi tecnici (CORS o permessi), anche se l’URL è raggiungibile da browser.<br />
                                            Il link viene comunque salvato e sarà visibile dove supportato. Per una preview sempre funzionante, usa link diretti (es. Imgur, server tuo, ecc.).
                                        </Alert>
                                        <TextField
                                            fullWidth
                                            label="URL Foto Profilo"
                                            value={formData.foto}
                                            onChange={(e) => handleFieldChange('foto', e.target.value)}
                                            error={false}
                                            helperText={fotoError ? "Immagine non caricabile: il link non è pubblico o il servizio non permette l'anteprima diretta. Puoi comunque salvare la squadra." : " "}
                                            placeholder="https://esempio.com/foto.jpg"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PhotoCameraIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: formData.foto && (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Anteprima">
                                                            <IconButton size="small">
                                                                <PreviewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    {/* Ordinamento */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Ordinamento"
                                            type="number"
                                            value={formData.ordinamento}
                                            onChange={(e) => handleFieldChange('ordinamento', e.target.value)}
                                            error={touched.ordinamento && !!errors.ordinamento}
                                            helperText={touched.ordinamento && errors.ordinamento || "Numero per ordinare i giocatori"}
                                            placeholder="0"
                                            inputProps={{ min: 0, step: 1 }}
                                        />
                                    </Grid>

                                    {/* Note */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Note"
                                            multiline
                                            rows={3}
                                            value={formData.note}
                                            onChange={(e) => handleFieldChange('note', e.target.value)}
                                            error={touched.note && !!errors.note}
                                            helperText={touched.note && errors.note || `${formData.note.length}/500 caratteri`}
                                            placeholder="Note aggiuntive sul giocatore..."
                                        />
                                    </Grid>

                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Colonna laterale - Preview e controlli */}
                    <Grid item xs={12} md={4}>

                        {/* Preview giocatore */}
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Anteprima Giocatore
                                </Typography>

                                <Avatar
                                    src={fotoPreview && !fotoError ? `${host}/image-proxy-mw?url=${encodeURIComponent(fotoPreview)}` : ''}
                                    alt={formData.nomeGiocatore}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        margin: '0 auto 16px',
                                        bgcolor: 'secondary.main',
                                        fontSize: '2rem'
                                    }}
                                    onError={handleFotoError}
                                    onLoad={handleFotoLoad}
                                >
                                    {!fotoPreview || fotoError ?
                                        (formData.nomeGiocatore ?
                                            formData.nomeGiocatore.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() :
                                            '👤'
                                        ) :
                                        null
                                    }
                                </Avatar>

                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {formData.nomeGiocatore || 'Nome Giocatore'}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
                                    {formData.teamId && (
                                        <Typography variant="body2" color="primary">
                                            {teams.find(t => t.id === parseInt(formData.teamId))?.nome || 'Squadra'}
                                        </Typography>
                                    )}
                                    {formData.teamId && formData.ruoloId && (
                                        <Typography variant="body2" color="text.secondary">•</Typography>
                                    )}
                                    {formData.ruoloId && (
                                        <Typography variant="body2" color="primary">
                                            {ruoli.find(r => r.id === parseInt(formData.ruoloId))?.nome || 'Ruolo'}
                                        </Typography>
                                    )}
                                </Box>

                                {formData.note && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        {formData.note.length > 100 ? `${formData.note.substring(0, 100)}...` : formData.note}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        {/* Impostazioni */}
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Impostazioni
                                </Typography>

                                <Box sx={{ space: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.flVisibile}
                                                onChange={(e) => handleFieldChange('flVisibile', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Giocatore visibile pubblicamente"
                                        sx={{ mb: 1 }}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.flAttivo}
                                                onChange={(e) => handleFieldChange('flAttivo', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Giocatore attivo"
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Errori */}
                        {hasErrors && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Correggere i seguenti errori:
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {Object.values(errors).map((error, index) => (
                                        <li key={index}>
                                            <Typography variant="body2">{error}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        {/* Azioni */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={!canSave || saving}
                                sx={{ py: 1.5 }}
                            >
                                {saving ? 'Salvataggio...' : 'Crea Giocatore'}
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={handleReset}
                                disabled={saving}
                            >
                                Reset Form
                            </Button>
                        </Box>
                    </Grid>

                </Grid>

                {/* Footer */}
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        * Campi obbligatori
                    </Typography>
                </Box>

            </motion.div>
        </Box>
    );
};